import { useState } from 'react';
import { UTILITY_TOKEN_ABI, resolveGovernanceAddresses, sendGovernanceCall } from '../lib/governance';
import { Banknote, Hourglass } from 'lucide-react';
import type { PrimaryTokenView } from '../lib/lookup-types';
import { liquidatePrimaryToken, resolveCdttConfiguration, type CdttStep } from '../lib/cdtt';
import { useWallet } from '../state/WalletContext';

/*
 * rules/rule15.txt section 4 + owner settlement workflow 2026-10-21: once the 365-day freeze is over the member settles
 * ONCE in the CDT Benefit card (Option A = Token Liquidity in BTCB, Option B = CDT). This block keeps the public
 * "Unfreeze token" call and, ONLY while the CDT Benefit contract is absent from the API, the native liquidation
 * (Option A) as the single on-chain action. The legacy live-value "Convert to CDTT" path was removed by the owner (Q3 = B).
 */
const STEP_TEXT: Record<CdttStep, string> = {
  requesting: 'Step 1/2 · confirm the redemption request in your wallet…',
  escrowed: 'Primary Token escrowed with the coordinator.',
  converting: 'Step 2/2 · confirm the liquidation in your wallet…',
  done: 'Liquidated. BTCB paid to your wallet; the Primary Token is finally redeemed.',
};

export function PostUnfreezeOptions({ token, onConverted, liquidationQuoteAtomic, cdtBenefitConfigured = false }: { token: PrimaryTokenView; onConverted?: () => void; liquidationQuoteAtomic?: string | null; /** docs/CDT_BENEFIT_SPEC.md s.3: Option A / B are settled in the CDT Benefit card instead. */ cdtBenefitConfigured?: boolean }) {
  const wallet = useWallet();
  const config = resolveCdttConfiguration();
  const [step, setStep] = useState<CdttStep | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [unfreezeNote, setUnfreezeNote] = useState<string | null>(null);
  async function unfreezeToken() {
    if (!wallet.address) { setError('Connect any wallet to send the unfreeze transaction.'); return; }
    setBusy(true); setError(null); setUnfreezeNote(null);
    try {
      const hash = await sendGovernanceCall(resolveGovernanceAddresses(), wallet.address as `0x${string}`, 'token', UTILITY_TOKEN_ABI, 'unfreeze', [BigInt(token.bdId.replace(/^BD-/i, ''))]);
      setUnfreezeNote(`Unfreeze sent: ${String(hash).slice(0, 12)}… - the settlement unlocks once the indexer sees the block (about 15 seconds).`);
      setTimeout(() => onConverted?.(), 15_000);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Unfreeze failed.');
    } finally {
      setBusy(false);
    }
  }
  const ownsToken = Boolean(wallet.address && token.wallet && wallet.address.toLowerCase() === token.wallet.toLowerCase());
  const why = !token.minted ? 'Primary Token not minted yet' : !token.redemptionEligible ? (token.statusCode === 1 ? `Frozen until ${token.unfreezeAt ? new Date(token.unfreezeAt).toLocaleDateString() : 'the unfreeze date'}` : token.status) : !ownsToken ? 'Connect the wallet that holds this token' : !config?.liquidation ? 'Liquidation contract not configured' : null;

  const canLiquidate = Boolean(config?.liquidation) && token.minted && token.redemptionEligible && ownsToken && !busy;
  async function liquidate() {
    if (!config || !wallet.address) return;
    setBusy(true); setError(null); setStep(null);
    try {
      await liquidatePrimaryToken(config, wallet.address as `0x${string}`, BigInt(token.bdId.replace(/^BD-/i, '')), (next) => setStep(next));
      onConverted?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Liquidation failed.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="post-unfreeze" data-testid="post-unfreeze-options">
      <div className="post-unfreeze-head"><strong>After the freeze period · maturity (rule 15 · rule 43)</strong></div>
      {token.unfreezeReady ? (
        <p className="empty-copy">The freeze period of this token is over. <button className="button primary small" type="button" disabled={busy} onClick={() => void unfreezeToken()}>Unfreeze token</button> Anyone may send this transaction (gas only); afterwards the holder settles once.{unfreezeNote ? ` ${unfreezeNote}` : ''}</p>
      ) : null}
      {cdtBenefitConfigured ? <p className="empty-copy">At maturity you settle <strong>once</strong> in the <a href="#cdt-benefit">CDT Benefit card</a> below: Option A (Token Liquidity in BTCB, locked CDT voided) or Option B (take CDT) - never both.</p> : null}
      <div className="post-unfreeze-grid" hidden={cdtBenefitConfigured}>
        <div className="option">
          <span><Banknote size={16} /></span>
          <strong>Option A — Take Token Liquidity (BTCB)</strong>
          <p>Redeem at the live value - Token Liquidity pool × your token&apos;s weight ÷ all active weight - paid in BTCB; the token is burned, the record stays.{liquidationQuoteAtomic ? ` Quote now: ${Number(liquidationQuoteAtomic) / 1e18} BTCB.` : ''}</p>
          <button className="button secondary small" type="button" disabled={!canLiquidate} onClick={() => void liquidate()} title={why ?? undefined}>{busy ? 'Working…' : 'Option A · take BTCB'}</button>
          {why ? <small>{why}</small> : null}
        </div>
        <div className="option">
          <span><Hourglass size={16} /></span>
          <strong>Hold</strong>
          <p>Keep the unfrozen token to benefit from ongoing liquidity growth. Nothing to sign. Option B (take CDT) opens once the CDT Benefit contract is wired into this network.</p>
          <button className="button ghost small" type="button" disabled>Default · no action</button>
        </div>
      </div>
      {step ? <p className="empty-copy">{STEP_TEXT[step]}</p> : null}
      {error ? <p className="token-identity-error">{error}</p> : null}
    </div>
  );
}
